import { ColDef, ValueGetterParams } from 'ag-grid-community'
import { intl } from '../../../../../../i18n'
import {
  ProgramManagementGridContext,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../../../programManagement'
import { BulkSheetColumnDef } from '../../../../../containers/BulkSheetView/model'
import { GRAYED_OUT_STYLE } from '../aggregationColumns/common'
import { dateValueFormatter } from '../../../../../containers/commons/AgGrid'

type RowValueGetter = ValueGetterParams<ProgramManagementRow>

// --- Basic columns ---

const codeColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.code',
  colId: 'code',
  field: 'code',
  headerName: intl.formatMessage({ id: 'programManagement.column.code' }),
  width: 100,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
}

const typeColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.type',
  colId: 'type',
  headerName: intl.formatMessage({ id: 'programManagement.column.type' }),
  width: 100,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
  valueGetter: (params: RowValueGetter) => {
    const type = params.data?.type
    if (!type) return ''
    const keyMap = {
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM]: 'programManagement.type.program',
      [PROGRAM_MANAGEMENT_ROW_TYPE.GROUP]: 'programManagement.type.group',
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT]: 'programManagement.type.project',
      [PROGRAM_MANAGEMENT_ROW_TYPE.WORKGROUP]:
        'programManagement.type.workgroup',
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROCESS]: 'programManagement.type.process',
    } as const
    return intl.formatMessage({ id: keyMap[type] })
  },
}

const statusColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.status',
  colId: 'status',
  field: 'status',
  headerName: intl.formatMessage({ id: 'programManagement.column.status' }),
  width: 100,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
  cellStyle: params =>
    params.data?.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP
      ? GRAYED_OUT_STYLE
      : null,
}

const projectManagerColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.projectManager',
  colId: 'projectManager',
  headerName: intl.formatMessage({
    id: 'programManagement.column.projectManager',
  }),
  width: 120,
  editable: false,
  valueGetter: (params: RowValueGetter) => {
    if (params.data?.type !== PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT) return ''
    return params.data?.projectManager?.name ?? ''
  },
  cellStyle: params =>
    params.data?.type !== PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT
      ? GRAYED_OUT_STYLE
      : null,
}

const scheduledStartDateColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.scheduledStartDate',
  colId: 'scheduledStartDate',
  headerName: intl.formatMessage({
    id: 'programManagement.column.scheduledStartDate',
  }),
  width: 110,
  editable: false,
  sortable: true,
  valueGetter: (params: RowValueGetter) =>
    params.data?.scheduledDate?.startDate ?? '',
  valueFormatter: params =>
    dateValueFormatter(
      params.value,
      (params.context as ProgramManagementGridContext)?.dateFormat
    ) || '',
}

const scheduledEndDateColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.scheduledEndDate',
  colId: 'scheduledEndDate',
  headerName: intl.formatMessage({
    id: 'programManagement.column.scheduledEndDate',
  }),
  width: 110,
  editable: false,
  sortable: true,
  valueGetter: (params: RowValueGetter) =>
    params.data?.scheduledDate?.endDate ?? '',
  valueFormatter: params =>
    dateValueFormatter(
      params.value,
      (params.context as ProgramManagementGridContext)?.dateFormat
    ) || '',
}

export const basicColumns: ColDef<ProgramManagementRow>[] = [
  codeColumn,
  typeColumn,
  statusColumn,
  projectManagerColumn,
  scheduledStartDateColumn,
  scheduledEndDateColumn,
]
